import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormHelperText,
  Divider,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { EmployeePickerComponent } from './EmployeePicker';
import { useTemplateDefaults } from './useTemplateDefaults';
import { slugifyImageName } from './slug';
import { Alert } from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { createFormField } from '@backstage/plugin-scaffolder-react/alpha';

export interface ServerItem {
  id: string;
  name: string;
  provider: string;
  environment: string;
  ip: string;
  runtimes: ('compose' | 'kubernetes')[];
  targets?: Record<string, string>;
}

export interface EnvironmentSetupValue {
  environment?: 'staging' | 'product' | 'all';
  server?: string;
  serverName?: string;
  serverIp?: string;
  runtime?: 'compose' | 'kubernetes';
  clusterUrl?: string;
  externalPort?: number;
  containerPort?: number;
  autoAssignPort?: boolean;
  projectAdminRefs?: string[];
  environmentMemberRefs?: string[];
  deployImmediately?: boolean;
  createDatabase?: boolean;
  databaseName?: string;
  databaseEngine?: 'postgres' | 'mongodb';
}

export interface EnvironmentServerPickerProps {
  onChange: (value: EnvironmentSetupValue) => void;
  formData?: EnvironmentSetupValue;
  formContext?: { formData?: { name?: string; repoName?: string } };
  required?: boolean;
  rawErrors?: string[];
  idSchema?: {
    $id: string;
  };
  schema?: {
    title?: string;
    description?: string;
  };
}

export const EnvironmentServerPickerComponent = (
  props: EnvironmentServerPickerProps,
) => {
  const { onChange, formData = {}, rawErrors, schema } = props;
  const { defaults } = useTemplateDefaults();
  const latest = useRef({ formData, onChange });
  latest.current = { formData, onChange };
  const projectName = props.formContext?.formData?.name || '';
  const repoName = props.formContext?.formData?.repoName || projectName;
  const image = defaults
    ? defaults.imageHub + '/' + slugifyImageName(repoName) + ':${IMAGE_TAG}'
    : 'Loading image repository...';
  const [portLoading, setPortLoading] = useState(false);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  // Project Provisioning strictly limits environment to staging
  const [servers, setServers] = useState<ServerItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchServers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const baseUrl = await discoveryApi.getBaseUrl('template-provisioning');
      const res = await fetchApi.fetch(
        `${baseUrl}/servers?environment=staging`,
      );
      if (!res.ok) {
        throw new Error(`Failed to load servers: HTTP ${res.status}`);
      }
      const data = await res.json();
      const items: ServerItem[] = (data.items || []).filter(
        (server: ServerItem) => server.environment === 'staging',
      );
      setServers(items);

      // Check if previously selected server is still valid under staging filter
      if (formData.server && !items.some(s => s.id === formData.server)) {
        onChange({
          ...formData,
          environment: 'staging',
          server: undefined,
          serverName: undefined,
          serverIp: undefined,
          runtime: undefined,
        });
      } else if (formData.environment !== 'staging') {
        onChange({
          ...formData,
          environment: 'staging',
        });
      }
    } catch (err: any) {
      setError(err.message || 'Error fetching server list from abc-api');
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi, formData, onChange]);

  useEffect(() => {
    fetchServers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!formData.autoAssignPort || !formData.server) return undefined;
    let active = true;
    setPortLoading(true);
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('template-provisioning');
        const response = await fetchApi.fetch(
          base +
            '/servers/' +
            encodeURIComponent(formData.server!) +
            '/available-port',
        );
        if (!response.ok)
          throw new Error('Unable to find an available host port');
        const result = await response.json();
        if (active)
          latest.current.onChange({
            ...latest.current.formData,
            externalPort: result.port,
          });
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (active) setPortLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [formData.autoAssignPort, formData.server, discoveryApi, fetchApi]);

  const selectedServer = servers.find(s => s.id === formData.server);
  const allowedRuntimes = ['compose'];
  const matchingServers = servers.filter(
    server => !formData.runtime || server.runtimes.includes(formData.runtime),
  );

  const handleServerChange = (e: ChangeEvent<{ value: unknown }>) => {
    const nextServerId = e.target.value as string;
    const serverObj = servers.find(s => s.id === nextServerId);
    let nextRuntime = formData.runtime;

    if (serverObj) {
      if (!nextRuntime || !serverObj.runtimes.includes(nextRuntime)) {
        nextRuntime = serverObj.runtimes[0] || 'compose';
      }
    }

    onChange({
      ...formData,
      environment: 'staging',
      server: nextServerId,
      serverName: serverObj?.name,
      serverIp: serverObj?.ip,
      runtime: nextRuntime,
    });
  };

  const handleRuntimeChange = (e: ChangeEvent<{ value: unknown }>) => {
    onChange({
      ...formData,
      environment: 'staging',
      runtime: e.target.value as 'compose' | 'kubernetes',
      server: undefined,
      serverName: undefined,
      serverIp: undefined,
    });
  };

  const handleExternalPortChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value ? parseInt(e.target.value, 10) : undefined;
    onChange({
      ...formData,
      externalPort: val,
    });
  };

  const hasErrors = rawErrors && rawErrors.length > 0;

  return (
    <Box marginY={2}>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
        {schema?.title ?? 'Environment Setup'}
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ marginBottom: 16 }}
      >
        {schema?.description ??
          'Configure deployment environment, server, runtime platform, and host port reservation.'}
      </Typography>

      {error && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => fetchServers()}
            >
              Retry
            </Button>
          }
          style={{ marginBottom: 16 }}
        >
          {error}
        </Alert>
      )}

      <Box mt={4} mb={2}>
        <Divider />
        <Box mt={3}>
          <Typography variant="h6">Access</Typography>
          <Typography variant="body2" color="textSecondary">
            Choose who can manage the project and access staging.
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <EmployeePickerComponent
            schema={{
              title: 'Project Admins',
              description:
                'Invite admins to manage all project environments, pipelines, variables and members.',
            }}
            formData={formData.projectAdminRefs || []}
            onChange={projectAdminRefs =>
              onChange({ ...formData, projectAdminRefs })
            }
          />
        </Grid>
        <Grid item xs={12}>
          <EmployeePickerComponent
            schema={{
              title: 'Environment Members',
              description:
                'Invite participants to access this staging environment.',
            }}
            formData={formData.environmentMemberRefs || []}
            onChange={environmentMemberRefs =>
              onChange({ ...formData, environmentMemberRefs })
            }
          />
        </Grid>
      </Grid>
      <Box mt={4} mb={2}>
        <Divider />
        <Box mt={3}>
          <Typography variant="h6">Deployment target</Typography>
          <Typography variant="body2" color="textSecondary">
            Choose the runtime and staging server for your project.
          </Typography>
        </Box>
      </Box>
      <FormControlLabel
        label="Deploy immediately after setup"
        control={
          <Checkbox
            color="primary"
            checked={formData.deployImmediately === true}
            onChange={event => onChange({ ...formData, deployImmediately: event.target.checked })}
          />
        }
      />
      <FormHelperText>When unchecked, setup creates the environment and Jenkins job without deploying.</FormHelperText>
      <Grid container spacing={2}>
        {/* Staging environment */}
        <Grid item xs={12} sm={6}>
          <TextField
            variant="outlined"
            fullWidth
            margin="dense"
            label="Deployment Environment"
            value="Staging (fixed)"
            InputProps={{ readOnly: true }}
            helperText="This template deploys to staging servers only."
          />
        </Grid>

        {/* Runtime Platform */}
        <Grid item xs={12} sm={6}>
          <FormControl
            variant="outlined"
            fullWidth
            margin="dense"
            error={Boolean(!formData.runtime && hasErrors)}
          >
            <InputLabel id="runtime-select-label">Runtime Platform</InputLabel>
            <Select
              labelId="runtime-select-label"
              id="runtime-select"
              value={formData.runtime || ''}
              onChange={handleRuntimeChange}
              label="Runtime Platform"
              disabled={loading}
            >
              {allowedRuntimes.includes('compose') && (
                <MenuItem value="compose">Docker Compose (compose)</MenuItem>
              )}
              {allowedRuntimes.includes('kubernetes') && (
                <MenuItem value="kubernetes">Kubernetes (k8s)</MenuItem>
              )}
            </Select>
            <FormHelperText>
              {selectedServer
                ? `Supported runtimes on ${
                    selectedServer.name
                  }: [${allowedRuntimes.join(', ')}]`
                : 'Select runtime first, then choose a compatible server'}
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* 2. Server Picker */}
        <Grid item xs={12} sm={6}>
          <FormControl
            variant="outlined"
            fullWidth
            margin="dense"
            error={Boolean(!formData.server && hasErrors)}
          >
            <InputLabel id="server-select-label">Staging Server</InputLabel>
            <Select
              labelId="server-select-label"
              id="server-select"
              value={formData.server || ''}
              onChange={handleServerChange}
              label="Staging Server"
              disabled={
                loading || !formData.runtime || matchingServers.length === 0
              }
            >
              {matchingServers.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name} ({s.provider.toUpperCase()}) &bull; {s.environment}{' '}
                  &bull; {s.ip}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {loading
                ? 'Loading servers from abc-api...'
                : servers.length === 0
                  ? 'No servers found for this environment'
                  : 'Select host server'}
            </FormHelperText>
          </FormControl>
        </Grid>

        {formData.runtime === 'kubernetes' && (
          <Grid item xs={12}>
            <TextField
              label="Kubernetes API server"
              fullWidth
              required
              variant="outlined"
              margin="dense"
              value={formData.clusterUrl || ''}
              onChange={event =>
                onChange({ ...formData, clusterUrl: event.target.value })
              }
              helperText="API server origin for the selected cluster, e.g. https://cluster.example.com:6443"
            />
          </Grid>
        )}
      </Grid>
      <Box mt={4} mb={2}>
        <Divider />
        <Box mt={3}>
          <Typography variant="h6">Networking</Typography>
          <Typography variant="body2" color="textSecondary">
            Configure the host port used to reach your application.
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={2}>
        {/* 4. External Port */}
        <Grid item xs={12} sm={6}>
          <TextField
            id="external-port-input"
            label="External Host Port"
            type="number"
            variant="outlined"
            margin="dense"
            fullWidth
            required
            value={formData.externalPort ?? ''}
            onChange={handleExternalPortChange}
            disabled={formData.autoAssignPort || portLoading}
            helperText={
              portLoading
                ? 'Finding an available port...'
                : formData.autoAssignPort
                  ? 'An available port is selected from the IP registry and reserved atomically when provisioning starts.'
                  : 'External host port (1-65535). Reserved across the server IP.'
            }
            inputProps={{ min: 1, max: 65535 }}
            error={Boolean(
              formData.externalPort &&
              (formData.externalPort < 1 || formData.externalPort > 65535),
            )}
          />
        </Grid>

        {/* Container Port (Optional) */}
        <Grid item xs={12} sm={6}>
          <TextField
            id="container-port-input"
            label="Internal Container Port (Read-only)"
            type="number"
            variant="outlined"
            margin="dense"
            fullWidth
            value={3000}
            InputProps={{ readOnly: true }}
            helperText="Internal application container port (default: 3000). Not checked for uniqueness."
            inputProps={{ min: 1, max: 65535 }}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            label="Automatically find an available host port"
            control={
              <Checkbox
                color="primary"
                checked={formData.autoAssignPort === true}
                onChange={event =>
                  onChange({
                    ...formData,
                    autoAssignPort: event.target.checked,
                    externalPort: undefined,
                  })
                }
              />
            }
          />
        </Grid>
      </Grid>
      <Box mt={4} mb={2}>
        <Divider />
        <Box mt={3}>
          <Typography variant="h6">Database</Typography>
          <Typography variant="body2" color="textSecondary">
            Optionally create a database for this environment.
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            label="Create database"
            control={
              <Checkbox
                checked={formData.createDatabase === true}
                onChange={event =>
                  onChange({
                    ...formData,
                    createDatabase: event.target.checked,
                    databaseEngine: event.target.checked
                      ? formData.databaseEngine || 'postgres'
                      : undefined,
                    databaseName: event.target.checked
                      ? formData.databaseName
                      : undefined,
                  })
                }
                color="primary"
              />
            }
          />
          <FormHelperText>
            Create a database on the selected staging server. Leave unchecked to
            skip database creation.
          </FormHelperText>
        </Grid>
        {formData.createDatabase && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                required
                variant="outlined"
                margin="dense"
                label="Database Engine"
                value={formData.databaseEngine || 'postgres'}
                onChange={event =>
                  onChange({
                    ...formData,
                    databaseEngine: event.target.value as
                      'postgres' | 'mongodb',
                  })
                }
              >
                <MenuItem value="postgres">PostgreSQL</MenuItem>
                <MenuItem value="mongodb">MongoDB</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                variant="outlined"
                margin="dense"
                label="Database Name"
                value={formData.databaseName || ''}
                onChange={event =>
                  onChange({ ...formData, databaseName: event.target.value })
                }
                inputProps={{ maxLength: 63 }}
                error={Boolean(
                  hasErrors &&
                  !/^[a-z][a-z0-9_]{0,62}$/.test(formData.databaseName || ''),
                )}
                helperText="Start with a lowercase letter; use lowercase letters, digits and underscores (up to 63 characters)."
              />
            </Grid>
          </>
        )}
      </Grid>
      <Box mt={4} mb={2}>
        <Divider />
        <Box mt={3}>
          <Typography variant="h6">Deployment details</Typography>
          <Typography variant="body2" color="textSecondary">
            These values are configured automatically.
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={2}>
        {[
          ['Docker Image Repository (Read-only)', image],
          [
            'Health Check URL (Read-only)',
            selectedServer && formData.externalPort
              ? 'http://' +
                (selectedServer.ip.includes(':')
                  ? '[' + selectedServer.ip + ']'
                  : selectedServer.ip) +
                ':' +
                formData.externalPort +
                '/api/health'
              : 'Select a server and host port',
          ],
          ['SSH User (Read-only)', 'jenkins'],
          ['SSH Port (Read-only)', '22'],
          [
            'Remote Deploy Directory (Read-only)',
            '/home/abc_workspace/' + repoName + '/staging',
          ],
          ['CPU Limit (Read-only)', '0.5'],
          ['Memory Limit (RAM, Read-only)', '1g'],
        ].map(([label, value]) => (
          <Grid item xs={12} sm={6} key={label}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label={label}
              value={value}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        ))}

      </Grid>
    </Box>
  );
};

export const environmentServerValidation = (value: any, validation: any) => {
  if (!value || typeof value !== 'object') {
    validation.addError('Environment configuration is required');
    return;
  }
  if (value.createDatabase === true) {
    if (
      !/^[a-z][a-z0-9_]{0,62}$/.test(value.databaseName || '') ||
      [
        'postgres',
        'template0',
        'template1',
        'admin',
        'local',
        'config',
      ].includes(value.databaseName)
    ) {
      validation.addError(
        'Enter a valid database name; system database names are reserved',
      );
    }
    if (
      value.databaseEngine &&
      !['postgres', 'mongodb'].includes(value.databaseEngine)
    ) {
      validation.addError('Choose PostgreSQL or MongoDB');
    }
  }
  if (!value.server) {
    validation.addError('Please select a deployment server');
  }
  if (!value.runtime) {
    validation.addError('Please select a runtime platform');
  }
  if (value.runtime === 'kubernetes') {
    try {
      const url = new URL(value.clusterUrl || '');
      if (
        !['https:', 'http:'].includes(url.protocol) ||
        url.username ||
        url.password ||
        url.pathname !== '/' ||
        url.search ||
        url.hash
      )
        throw new Error();
    } catch {
      validation.addError('Provide the Kubernetes API server origin');
    }
  }
  if (!value.externalPort && !value.autoAssignPort) {
    validation.addError('External port is required');
  } else if (value.externalPort !== undefined) {
    const p = Number(value.externalPort);
    if (!Number.isInteger(p) || p < 1 || p > 65535) {
      validation.addError(
        'External port must be an integer between 1 and 65535',
      );
    }
  }
};

export const EnvironmentServerPicker = createFormField({
  name: 'EnvironmentServerPicker',
  component: EnvironmentServerPickerComponent as any,
  validation: environmentServerValidation as any,
});
